import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GS } from './getStartedTheme'
import logoMark from '../layouts/images/logo1-close.png'
import './getStarted.css'

export type GetStartedStep = 1 | 2 | 3

interface GetStartedLayoutProps {
  title: string
  subtitle?: string
  step?: GetStartedStep
  /** @deprecated kept for callers */
  maxWidth?: number
  children: React.ReactNode
  panel?: boolean
  wide?: boolean
}

const STEPS = [
  { n: 1, key: 'organization' },
  { n: 2, key: 'verify' },
  { n: 3, key: 'welcome' },
] as const

const GetStartedLayout: React.FC<GetStartedLayoutProps> = ({
  title,
  subtitle,
  step,
  children,
  panel = true,
  wide = false,
}) => {
  const { t, i18n } = useTranslation()
  const [lang, setLang] = useState(
    (localStorage.getItem('i18nextLng') || i18n.language || 'en').slice(0, 2)
  )

  const setLanguage = (code: string) => {
    setLang(code)
    i18n.changeLanguage(code)
    localStorage.setItem('i18nextLng', code)
  }

  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const root = document.getElementById('root')
    html.classList.add('gs-no-zoom')
    const prev = {
      htmlBg: html.style.backgroundColor,
      bodyBg: body.style.backgroundColor,
      rootBg: root?.style.backgroundColor ?? '',
    }
    html.style.backgroundColor = GS.navy
    body.style.backgroundColor = GS.navy
    if (root) root.style.backgroundColor = GS.navy
    return () => {
      html.classList.remove('gs-no-zoom')
      html.style.backgroundColor = prev.htmlBg
      body.style.backgroundColor = prev.bodyBg
      if (root) root.style.backgroundColor = prev.rootBg
    }
  }, [])

  const langSwitch = (
    <div className="gs-lang" role="group" aria-label="Language">
      <button
        type="button"
        className={`gs-lang-btn${lang === 'en' ? ' is-active' : ''}`}
        onClick={() => setLanguage('en')}
        aria-pressed={lang === 'en'}
      >
        EN
      </button>
      <button
        type="button"
        className={`gs-lang-btn${lang === 'fr' ? ' is-active' : ''}`}
        onClick={() => setLanguage('fr')}
        aria-pressed={lang === 'fr'}
      >
        FR
      </button>
    </div>
  )

  return (
    <div className="gs-shell">
      <aside className="gs-brand-rail">
        <div className="gs-brand-glow" aria-hidden />
        <div className="gs-brand-inner">
          <div className="gs-logo">
            <div className="gs-logo-mark">
              <img src={logoMark} alt="" />
            </div>
            <span className="gs-logo-name">INCYIA</span>
            <sup className="gs-logo-sup">®</sup>
          </div>

          <div>
            <div className="gs-brand-eyebrow">
              {t('getStarted.brand.eyebrow', 'GRC platform')}
            </div>
            <h2 className="gs-brand-headline">
              {t(
                'getStarted.brand.headline',
                'Governance, risk and compliance — ready for your team.'
              )}
            </h2>
            <p className="gs-brand-body">
              {t(
                'getStarted.brand.body',
                'Create your organization space in a few steps. Verify your email, then explore plans with our team.'
              )}
            </p>
          </div>

          {step != null ? (
            <div className="gs-steps">
              {STEPS.map((s) => {
                const done = step > s.n
                const active = step === s.n
                return (
                  <div
                    key={s.n}
                    className={`gs-step${active ? ' is-active' : ''}${
                      done ? ' is-done' : ''
                    }`}
                  >
                    <div className="gs-step-n">{s.n}</div>
                    <span className="gs-step-label">
                      {t(`getStarted.steps.${s.key}`, {
                        defaultValue:
                          s.key === 'organization'
                            ? 'Organization'
                            : s.key === 'verify'
                              ? 'Verify'
                              : 'Welcome',
                      })}
                    </span>
                  </div>
                )
              })}
            </div>
          ) : null}
        </div>
      </aside>

      <main className="gs-main">
        <div className="gs-mobile-brand">
          <div className="gs-logo-mark" style={{ width: 32, height: 32 }}>
            <img
              src={logoMark}
              alt=""
              style={{ height: 22, width: 22 }}
            />
          </div>
          <span
            className="gs-logo-name"
            style={{ fontSize: 16, letterSpacing: '0.1em' }}
          >
            INCYIA
          </span>
          <div className="gs-mobile-lang">{langSwitch}</div>
        </div>

        <div className="gs-main-topbar">{langSwitch}</div>

        <div className={`gs-content${wide ? ' is-wide' : ''}`}>
          <div className="gs-page-head">
            {step != null ? (
              <p className="gs-page-eyebrow">
                <span className="gs-page-eyebrow-dot">{step}</span>
                {t(
                  'getStarted.pageEyebrow',
                  'Step {{step}} of 3 · Getting started',
                  { step }
                )}
              </p>
            ) : null}
            <h1 className="gs-page-title">{title}</h1>
            <div className="gs-page-title-bar" aria-hidden />
            {subtitle ? <p className="gs-page-sub">{subtitle}</p> : null}
          </div>

          {panel ? <div className="gs-panel">{children}</div> : children}
        </div>
      </main>
    </div>
  )
}

export default GetStartedLayout
